import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp } from "react-bootstrap-icons";
import CommonModal from "../Modals/CommonModal";
import AddLineLayout from "../../Layout/AddLineLayout";
import "./CustomDropdown.css";
import Loader from "../GlobalLoader/Loader";
interface DataRow {
  [key: string]: any;
}

interface ColumnConfig {
  columnName: string; // Display name for the column
  selector: string; // Key to access the data
}

interface CustomDropdownProps {
  handleSelect: (selectedItem: Record<string, any>) => void;
  data: DataRow[];
  customClassName?: string;
  selectableColumns?: ColumnConfig[]; // Array of column config objects
  buttonName?: string;
  modalContent?: React.ReactNode;
  isDisabled?: boolean;
  placeholder?: string;
  name?: string;
  onDropdownOpen?: () => void;
}
const CustomDropdown: React.FC<CustomDropdownProps> = ({
  handleSelect,
  data,
  selectableColumns,
  buttonName,
  customClassName = "",
  modalContent,
  isDisabled = false,
  placeholder = "Select",
  name = "",
  onDropdownOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [show, setShow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const hasCalledOnOpen = useRef(false);

  // Update click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is on scrollbar by checking if click is at the edge of viewport
      const isClickOnScrollbar =
        event.clientX >= document.documentElement.offsetWidth;

      // Don't close if click is on scrollbar
      if (isClickOnScrollbar) {
        return;
      }

      // Check if click is outside both the input and options list
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        optionsRef.current &&
        !optionsRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).classList.contains(
          "custom-dropdown-menu"
        ) &&
        !(event.target as HTMLElement).classList.contains(
          "customDropDownMenuWidth"
        )
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);

      // Add wheel event listener to prevent closing on scroll
      const handleWheel = (e: WheelEvent) => {
        e.stopPropagation();
      };
      document.addEventListener("wheel", handleWheel, { passive: true });

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("wheel", handleWheel);
      };
    }
  }, [isOpen]);

  // Add this useEffect for auto-scrolling
  useEffect(() => {
    if (isOpen && optionsRef.current) {
      optionsRef.current.addEventListener("wheel", (e) => {
        e.stopPropagation();
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && onDropdownOpen && !hasCalledOnOpen.current) {
      if (filteredData.length === 0) {
        onDropdownOpen();
      }
      hasCalledOnOpen.current = true;
    }
    if (!isOpen) {
      hasCalledOnOpen.current = false;
    }
  }, [isOpen, onDropdownOpen]);

  const handleShow = () => {
    setShow(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setShow(false);
  };

  // Determine column headers based on selectableColumns or default to all keys from the first data item
  const allKeys = data.length > 0 ? Object.keys(data[0]) : [];
  const keysToDisplay =
    selectableColumns && selectableColumns.length > 0
      ? selectableColumns.filter((col) => allKeys.includes(col.selector)) // Use only the columns provided in selectableColumns
      : allKeys.map((key) => ({
          columnName: key.charAt(0).toUpperCase() + key.slice(1),
          selector: key,
        })); // Default to all keys if selectableColumns is not provided

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleItemSelect = (item: any) => {
    const displayValue =
      selectableColumns && selectableColumns.length > 0
        ? item[selectableColumns[0].selector] // Use the first column from selectableColumns
        : Object.values(item).find((value) => typeof value === "string") ||
          "Selected Item"; // Fallback to the first string valu
    // setSearchTerm(item.location || item.item || item.ItemCode || item.inv_acct);
    setSearchTerm(displayValue);
    setIsOpen(false);
    handleSelect(item); // Call the passed-in handleSelect function
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    if (!isOpen) {
      setIsOpen(true); // Open the dropdown when typing
    }
  };

  // Filter data based on the search term
  const filteredData = data?.filter((item) => {
    return Object.values(item).some(
      (value) =>
        (typeof value === "string" &&
          value?.toLowerCase().includes(searchTerm?.toLowerCase())) ||
        (typeof value === "number" && value.toString().includes(searchTerm))
    );
  });

  return (
    <>
      <div
        className={`${customClassName} custom-dropdown-container border rounded`}
        ref={dropdownRef}
      >
        <div className="custom-dropdown-toggle rounded border-0">
          <input
            type="text"
            placeholder={searchTerm || placeholder}
            value={searchTerm}
            onChange={handleSearchChange}
            onClick={toggleDropdown}
            className={`${
              isDisabled ? "disabled" : null
            } custom-dropdown-search-input`}
            name={name}
          />
          <span className="custom-dropdown-arrow" onClick={toggleDropdown}>
            {isOpen && !isDisabled ? <ChevronUp /> : <ChevronDown />}
          </span>
        </div>
        {isOpen && !isDisabled && (
          <div
            ref={optionsRef}
            className={`${
              customClassName.includes("customDropDownMenuWidth")
                ? "customDropDownMenuWidth mb-3 rounded mt-2"
                : "custom-dropdown-menu mb-3 rounded mt-2 pt-0"
            }`}
            onClick={(e) => e.stopPropagation()} // Prevent click from bubbling
          >
            <div className="custom-dropdown-row custom-dropdown-header bg-white fw-medium">
              {keysToDisplay.map((col, index) => (
                <div
                  key={index}
                  className="custom-dropdown-cell border-bottom pb-2"
                >
                  {col.columnName}
                </div>
              ))}
            </div>
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <div
                  key={index}
                  className="cursor-pointer custom-dropdown-row border-bottom py-3"
                  onClick={() => handleItemSelect(item)}
                >
                  {keysToDisplay.map((col, idx) => (
                    <div key={idx} className="custom-dropdown-cell">
                      {item[col.selector]}
                    </div>
                  ))}
                </div>
              ))
            ) : data.length === 0 ? (
              <Loader />
            ) : (
              <p className="text-center py-3 mb-0">No data found</p>
            )}
            {buttonName && modalContent && (
              <>
                <p
                  className="fw-medium cursor-pointer pt-3 mb-0 w-fit"
                  onClick={handleShow}
                >
                  {buttonName}
                </p>
                <CommonModal
                  modalSize="modal-xl"
                  handleClose={handleClose}
                  show={show}
                  heading="Add Line Items"
                  content={
                    modalContent || <AddLineLayout handleClose={handleClose} />
                  }
                />
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default CustomDropdown;
